import {delay} from "@/utils/helpers";
import {GENOGRAM_EXPORT_DIAGNOSTIC_PREFIX} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramExportConstants";

export default class GenogramTileCaptureManager {

	constructor(options = {}) {
		this.logger = options.logger || null
	}

	async captureTiles(cy, plan, options = {}) {
		let viewportState = this.getViewportState(cy)
		let runtimeViewport = this.getRuntimeViewportMetrics(cy)
		let scaleInputViewportWidth = Math.max(runtimeViewport.exportViewportWidth || 1, 1)
		let scaleInputViewportHeight = Math.max(runtimeViewport.exportViewportHeight || 1, 1)
		let scale = plan.tileWidth / scaleInputViewportWidth
		let exportOptions = this.buildExportOptions(scale)
		let tiles = []
		let totalBlobBytes = 0
		let exportPath = this.getExportPath(cy)

		this.logger?.log("TileCaptureStarted", {
			tileCount: plan.tiles.length,
			tileWidth: plan.tileWidth,
			tileHeight: plan.tileHeight,
			scale,
			scaleInputViewportWidth,
			scaleInputViewportHeight,
			exportPath,
			...runtimeViewport,
		})

		try {
			for (let tile of plan.tiles) {
				let positionedViewport = await this.positionViewportForTile(cy, tile, plan)
				let blob = await this.captureTileBlob(cy, exportOptions)
				totalBlobBytes += blob.size
				tiles.push({
					...tile,
					plannedTileWidth: plan.tileWidth,
					plannedTileHeight: plan.tileHeight,
					blob,
				})
				this.logger?.log("TileCaptured", {
					tileIndex: tile.index,
					row: tile.row,
					column: tile.column,
					left: tile.left,
					top: tile.top,
					width: tile.width,
					height: tile.height,
					plannedTileWidth: plan.tileWidth,
					plannedTileHeight: plan.tileHeight,
					exportScale: scale,
					exportPath,
					...positionedViewport,
					blobBytes: blob.size,
					totalBlobBytes,
				})

				if (typeof options.onTileCaptured === "function") {
					await options.onTileCaptured({
						tile,
						tileCount: plan.tiles.length,
						capturedCount: tiles.length,
					})
				}
			}
		} finally {
			this.restoreViewportState(cy, viewportState)
		}

		this.logger?.log("TileCaptureCompleted", {
			tileCount: tiles.length,
			totalBlobBytes,
		})

		return tiles
	}

	buildExportOptions(scale) {
		return {
			output: "blob-promise",
			bg: "white",
			full: false,
			scale,
			ignoreUnsupportedLayers: true,
			ignoreUnsupportedLayerOrder: true,
		}
	}

	getViewportState(cy) {
		let pan = cy.pan()

		return {
			pan: {
				x: pan.x,
				y: pan.y,
			},
			zoom: cy.zoom(),
		}
	}

	async positionViewportForTile(cy, tile, plan) {
		let runtimeViewport = this.getRuntimeViewportMetrics(cy)
		let exportViewportWidth = Math.max(runtimeViewport.exportViewportWidth || 1, 1)
		let zoom = exportViewportWidth / plan.tileWidth
		let targetPan = {
			x: -tile.left * zoom,
			y: -tile.top * zoom,
		}

		cy.viewport({
			zoom,
			pan: targetPan,
		})
		cy.resize()
		await delay(0)
		await delay(20)

		return {
			targetZoom: zoom,
			targetPanX: targetPan.x,
			targetPanY: targetPan.y,
			actualZoom: cy.zoom(),
			actualPanX: cy.pan()?.x,
			actualPanY: cy.pan()?.y,
			...this.getRuntimeViewportMetrics(cy),
		}
	}

	async captureTileBlob(cy, exportOptions) {
		this.logger?.log("TileCaptureSourceState", {
			exportPath: this.getExportPath(cy),
			exportScale: exportOptions.scale,
			...this.getRuntimeViewportMetrics(cy),
			...this.getCanvasSurfaceMetrics(cy),
		})
		let blob = cy.layers ? await this.captureLayersBlob(cy, exportOptions) : await this.captureCoreBlob(cy, exportOptions)

		if (!(blob instanceof Blob)) {
			throw new Error(`${GENOGRAM_EXPORT_DIAGNOSTIC_PREFIX} Expected tile capture Blob`)
		}

		return blob
	}

	async captureLayersBlob(cy, exportOptions) {
		return await cy.layers().jpg(exportOptions)
	}

	async captureCoreBlob(cy, exportOptions) {
		return await cy.jpg(exportOptions)
	}

	restoreViewportState(cy, viewportState) {
		cy.viewport({
			zoom: viewportState.zoom,
			pan: {
				x: viewportState.pan.x,
				y: viewportState.pan.y,
			},
		})
		cy.resize()
	}

	getRuntimeViewportMetrics(cy) {
		let container = cy.container?.()
		let rect = container?.getBoundingClientRect?.() || null
		let viewportWidth = cy.width()
		let viewportHeight = cy.height()
		let containerClientWidth = container?.clientWidth || 0
		let containerClientHeight = container?.clientHeight || 0
		let containerRectWidth = Math.round(rect?.width || 0)
		let containerRectHeight = Math.round(rect?.height || 0)

		return {
			viewportWidth,
			viewportHeight,
			exportViewportWidth: containerClientWidth || containerRectWidth || viewportWidth,
			exportViewportHeight: containerClientHeight || containerRectHeight || viewportHeight,
			devicePixelRatio: this.getDevicePixelRatio(),
			containerClientWidth,
			containerClientHeight,
			containerOffsetWidth: container?.offsetWidth || 0,
			containerOffsetHeight: container?.offsetHeight || 0,
			containerRectWidth,
			containerRectHeight,
		}
	}

	getDevicePixelRatio() {
		let ratio = 1

		if (typeof window !== "undefined" && typeof window.devicePixelRatio === "number") {
			ratio = window.devicePixelRatio
		}

		return ratio
	}

	getExportPath(cy) {
		return cy.layers ? "layers" : "core"
	}

	getCanvasSurfaceMetrics(cy) {
		let container = cy.container?.()
		let canvases = container ? Array.from(container.querySelectorAll("canvas")) : []
		let canvasMetrics = canvases.map((canvas, index) => ({
			index,
			width: canvas.width || 0,
			height: canvas.height || 0,
			clientWidth: canvas.clientWidth || 0,
			clientHeight: canvas.clientHeight || 0,
			offsetWidth: canvas.offsetWidth || 0,
			offsetHeight: canvas.offsetHeight || 0,
			className: canvas.className || "",
		}))

		return {
			containerCanvasCount: canvases.length,
			containerCanvasMetrics: canvasMetrics,
		}
	}
}
